import React, { useState } from "react";
import numeral from "numeral";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { components } from "../../types/api";
import { AssociatedCampaignWithRelations } from "../../store/apis/briefingApi";
import { useUpdateAssociatedCampaignGoalMutation } from "../../store/apis/briefingApi";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";

type BriefingDetailResponse = components["schemas"]["BriefingDetailResponse"];

interface Props {
  briefing: BriefingDetailResponse;
}

export const BriefingRealesTable: React.FC<Props> = ({ briefing }) => {
  const [updateGoal] = useUpdateAssociatedCampaignGoalMutation();
  const dispatch = useDispatch();

  // Local state to hold temporary edits for manual "realPlatformInversion"
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState<Record<string, boolean>>({});
  const [isSavingAll, setIsSavingAll] = useState(false);

  const parseLocalizedNumber = (val: string): number => {
    const result = numeral(val).value();
    return result === null ? NaN : result;
  };

  if (
    !briefing ||
    !briefing.associatedCampaigns ||
    briefing.associatedCampaigns.length === 0
  ) {
    return null;
  }

  const handleEditChange = (campaignId: string, value: string) => {
    setEdits((prev) => ({ ...prev, [campaignId]: value }));
  };

  const handleSave = async (campaign: AssociatedCampaignWithRelations) => {
    const rawValue = edits[campaign.id];
    if (rawValue === undefined) return; // No edits made

    const numericValue = parseLocalizedNumber(rawValue);
    if (isNaN(numericValue) && rawValue !== "") {
      dispatch(
        setToastMessage({
          message: "Por favor ingresa un valor numérico válido.",
          severity: "error",
        })
      );
      return;
    }

    if (!campaign.goal) {
      dispatch(
        setToastMessage({
          message:
            "No se puede actualizar el valor real porque no hay goal configurado.",
          severity: "error",
        })
      );
      return;
    }

    setIsSaving((prev) => ({ ...prev, [campaign.id]: true }));

    try {
      await updateGoal({
        params: {
          briefingId: briefing.id,
          associatedId: campaign.id,
        },
        body: {
          realPlatformInversion: rawValue === "" ? null : numericValue,
        },
      }).unwrap();

      dispatch(
        setToastMessage({
          message: "Valor real actualizado exitosamente.",
          severity: "success",
        })
      );
      // Clear edit state to show saved value
      setEdits((prev) => {
        const next = { ...prev };
        delete next[campaign.id];
        return next;
      });
    } catch (error) {
      console.error("Failed to update real platform inversion:", error);
      dispatch(
        setToastMessage({
          message: "Error al actualizar el valor real.",
          severity: "error",
        })
      );
    } finally {
      setIsSaving((prev) => ({ ...prev, [campaign.id]: false }));
    }
  };

  const handleSaveAll = async () => {
    const editKeys = Object.keys(edits);
    if (editKeys.length === 0) return;

    setIsSavingAll(true);
    let allSuccess = true;

    for (const campaignId of editKeys) {
      const campaign = briefing.associatedCampaigns?.find(
        (c) => c.id === campaignId
      );
      if (!campaign || !campaign.goal) continue;

      const rawValue = edits[campaignId];
      const numericValue = parseLocalizedNumber(rawValue);

      if (isNaN(numericValue) && rawValue !== "") {
        allSuccess = false;
        continue; // Skip invalid, try next
      }

      setIsSaving((prev) => ({ ...prev, [campaignId]: true }));

      try {
        await updateGoal({
          params: {
            briefingId: briefing.id,
            associatedId: campaignId,
          },
          body: {
            realPlatformInversion: rawValue === "" ? null : numericValue,
          },
        }).unwrap();

        // Clear this edit upon success
        setEdits((prev) => {
          const next = { ...prev };
          delete next[campaignId];
          return next;
        });
      } catch (error) {
        console.error(
          `Failed to update real platform inversion for ${campaignId}:`,
          error
        );
        allSuccess = false;
      } finally {
        setIsSaving((prev) => ({ ...prev, [campaignId]: false }));
      }
    }

    if (allSuccess) {
      dispatch(
        setToastMessage({
          message: "Todos los valores se han guardado exitosamente.",
          severity: "success",
        })
      );
    } else {
      dispatch(
        setToastMessage({
          message:
            "Ocurrió un error al guardar algunos valores. Por favor, revisa.",
          severity: "warning",
        })
      );
    }

    setIsSavingAll(false);
  };

  const hasEdits = Object.keys(edits).length > 0;

  const formatCurrency = (value: number, currencyCode: string = "EUR") =>
    new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    }).format(value);

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Inversión Real</Typography>
        {hasEdits && (
          <Button
            variant="contained"
            color="primary"
            startIcon={
              isSavingAll ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSaveAll}
            disabled={isSavingAll}
          >
            Guardar Todos ({Object.keys(edits).length})
          </Button>
        )}
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Campaña</strong>
              </TableCell>
              <TableCell>
                <strong>ID Origen</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Previsto (Platform Inversion)</strong>
              </TableCell>
              <TableCell align="right" sx={{ width: 250 }}>
                <strong>Real</strong>
              </TableCell>
              <TableCell align="center" sx={{ width: 80 }}>
                <strong>Acciones</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {briefing.associatedCampaigns?.map(
              (campaign: AssociatedCampaignWithRelations) => {
                // Si tiene internalCampaignId, asumimos que está conectada y usamos realMetrics de la API.
                // Si no, mostramos input editable usando goal.realPlatformInversion.

                const isConnected = !!campaign.internalCampaignId;

                const apiRealCost = campaign.realMetrics?.campaignInvestment;
                const hasApiData =
                  isConnected &&
                  apiRealCost !== undefined &&
                  apiRealCost !== null;

                const manualValue = campaign.goal?.realPlatformInversion;
                const editValue = edits[campaign.id];

                const previsto = campaign.goal?.platformInversion || 0;
                const currency = campaign.goal?.currency || "EUR";

                return (
                  <TableRow key={campaign.id}>
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {campaign.sourceId || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(previsto, currency)}
                    </TableCell>
                    <TableCell align="right">
                      {hasApiData ? (
                        <Tooltip title="Valor obtenido automáticamente vía API">
                          <Typography sx={{ color: "text.secondary" }}>
                            {formatCurrency(apiRealCost as number, currency)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <TextField
                          size="small"
                          type="text"
                          placeholder="0,00"
                          value={
                            editValue !== undefined
                              ? editValue
                              : (manualValue ?? "")
                          }
                          onChange={(e) =>
                            handleEditChange(campaign.id, e.target.value)
                          }
                          disabled={isSaving[campaign.id]}
                          InputProps={{
                            endAdornment: (
                              <Typography variant="caption" sx={{ ml: 1 }}>
                                {currency}
                              </Typography>
                            ),
                          }}
                          sx={{ width: 180 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {!hasApiData &&
                        editValue !== undefined &&
                        (isSaving[campaign.id] || isSavingAll ? (
                          <CircularProgress size={24} />
                        ) : (
                          <IconButton
                            color="primary"
                            onClick={() => handleSave(campaign)}
                            size="small"
                            disabled={isSavingAll}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        ))}
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
